import { useState } from "react";
import { Button } from "@wmart/ui";
import { api, ApiError } from "../lib/api-client.js";
import { useToast } from "../lib/toast.js";

/**
 * DemoteToDraftButton — POST /v1/admin/invoices/:id/demote-to-draft.
 *
 * Renders inline (no modal) — the operation has no input parameters; the
 * server enforces the precondition set (status === issued, no payments,
 * no reminder sent). 422 responses surface via the toast provider with
 * the failure reason ("invoice.has_payments" etc.).
 *
 * Confirmation step: the action is intentionally cautious (it un-issues a
 * gap-free HMRC invoice number for re-use). We use `window.confirm` rather
 * than a full modal because the action is single-click + reversible (issue
 * again to get the next number) — matching the precedent set by lightweight
 * admin ops in OrderDetail (FulfilOrderModal, etc. use modals for forms;
 * single-action POSTs don't always need one).
 */
export function DemoteToDraftButton({
  invoiceId,
  onSuccess,
}: {
  invoiceId: string;
  onSuccess: () => void | Promise<void>;
}) {
  const { notify, notifyProblem } = useToast();
  const [busy, setBusy] = useState(false);

  async function onClick() {
    const ok = window.confirm(
      "Demote this issued invoice back to draft? The invoice number will be retained but the buyer will no longer see it as issued.",
    );
    if (!ok) return;
    setBusy(true);
    try {
      await api.post(`/v1/admin/invoices/${invoiceId}/demote-to-draft`, {});
      notify("success", "Invoice demoted to draft");
      await onSuccess();
    } catch (err) {
      if (err instanceof ApiError && err.problem) notifyProblem(err.problem);
      else notify("error", "Demote failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Button
      variant="secondary"
      onClick={() => void onClick()}
      disabled={busy}
      loading={busy}
    >
      Demote to draft
    </Button>
  );
}
